/* eslint-disable react/jsx-props-no-spreading,max-len */
/* eslint-disable react/destructuring-assignment */
import {
    MutableRefObject,
    useMemo,
    memo,
} from 'react'
import Divider from '@mui/material/Divider'
import {
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import clsx from 'clsx'
import noop from 'lodash/noop'
import useRowSelection from 'app/PoC/DataGridDemoPages/components/SkyNetDataGrid/hooks/useRowSelection'

import useDataGridColumns from 'app/PoC/DataGridDemoPages/components/hooks/useDataGridColumns'
import SkyBaseDataGrid, {
    SkyBaseDataGridProps,
    ExtendedGridApiPro,
    ColumnConfigMappingType, ExtendedGridColDef,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid'
import SkyNetDataGridPagination from 'app/PoC/DataGridDemoPages/components/SkyNetDataGridPagination'

import {
    DataGridSupportedFeatures,
} from '../SkyNetMasterDataGrid/SkyNetMasterDataGrid.types'
import {
    featuresToHiddenColumns,
} from '../utils/utils'

import usePersistedVisibleColumns from '../hooks/useVisibiltyModel/usePersistedVisibleColumns'
import useStyles from './SkyNetDataGrid.styles'

export enum SelectionType {
    Radio = 'radio',
    Checkbox = 'checkbox',
}

export type SkyNetDataGridProps = Omit<SkyBaseDataGridProps,
    'visibilityModel' | 'onColumnVisibilityModelChange' | 'columns'> & {
    featuresLists?: DataGridSupportedFeatures[]
    columns: ColumnConfigMappingType[],
    selectionType?: SelectionType
    selectedRows?: (string | number)[],
    onSelectedRow?: (value: any) => void,
}

const emptyArray: DataGridSupportedFeatures[] = []

export default memo((props: Readonly<SkyNetDataGridProps>) => {
    const {
        featuresLists = emptyArray,
        gridApiRef,
        customDataSource, columns, persistentKey,
        selectionType,
        onSelectedRow = noop,
        disabled,
        classNames,
        disableMultipleRowSelection,
        selectedRows,
        selectionModelType,
        ...rest
    } = props

    const {
        CustomToolbar, toolbarProps,
    } = props
    const newApiRef = useGridApiRef<ExtendedGridApiPro>()
    const apiRef: MutableRefObject<ExtendedGridApiPro> = gridApiRef || newApiRef
    const {
        classes,
    } = useStyles()

    const additionalColumnsForFilters: ColumnConfigMappingType[] = useMemo(() => {
        return featuresToHiddenColumns(featuresLists)
    }, [featuresLists])

    const columnsWithLabels = useDataGridColumns({
        columnsDef: columns,
        customDataSource,
        filtersUnrelatedToColumns: additionalColumnsForFilters,
    })

    const [
        visibilityModel,
        onColumnVisibilityModelChange,
    ] = usePersistedVisibleColumns({
        columns: columnsWithLabels,
        persistentKey,
    })

    const extendedToolbarProps = useMemo(() => {
        if ('CustomToolbar' in props && CustomToolbar) {
            return {
                CustomToolbar,
            }
        }

        if ('toolbarProps' in props) {
            return {
                toolbarProps: {
                    ...toolbarProps,
                    toolbarControls: [
                        ...(toolbarProps?.toolbarControls || []),
                        (toolbarProps?.toolbarControls?.length > 0) ? (
                            <Divider
                                orientation="vertical"
                                variant="middle"
                                sx={{
                                    mx: 1, height: '20px',
                                }}
                            />
                        ) : null,
                    ],
                },
            }
        }

        return {}
    }, [
        CustomToolbar,
        props,
        toolbarProps,
    ])

    const {
        customRowSelectionColumn,
        selectionModel,
        onRowSelectionModelChange,
        disableRowSelectionOnClick,
    } = useRowSelection({
        selectionType,
        apiRef,
        onSelectedRow,
        disabled,
        selectedRows,
        selectionModelType,
    })

    const columnsWithSelection = useMemo(() => {
        return [
            ...(selectionType ? [customRowSelectionColumn] : []),
            ...columnsWithLabels,
        ] as ExtendedGridColDef[]
    }, [
        columnsWithLabels,
        customRowSelectionColumn,
        selectionType,
    ])

    return (
        <SkyBaseDataGrid
            persistentKey={persistentKey}
            {...rest}
            {...extendedToolbarProps}
            columns={columnsWithSelection}
            customDataSource={customDataSource}
            gridApiRef={apiRef}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            visibilityModel={visibilityModel}
            classNames={{
                ...(classNames || {}),
                container: clsx(classes.container,
                    selectionType === SelectionType.Radio && classes.withRadioSelection,
                    classNames?.container),
            }}
            Pagination={SkyNetDataGridPagination}
            enableSortFilterPersistence={false}
            checkboxSelection={Boolean(selectionType)}
            disableMultipleRowSelection={disableMultipleRowSelection || selectionType === SelectionType.Radio}
            onRowSelectionModelChange={onRowSelectionModelChange}
            rowSelectionModel={selectionModel}
            disabled={disabled}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
        />
    )
})
